(function () {
  const JUNIP_CONTENT_SELECTOR = [
    '[class*="review-card"]',
    '[class*="ReviewCard"]',
    '[data-review-id]',
    '.junip-review-list-item',
  ].join(',');
  const MAX_FILES = 5;
  const MAX_BYTES = 10 * 1024 * 1024;
  const ALLOWED_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp']);

  function syncJunip(section) {
    if (section.dataset.lpReviewsProvider !== 'junip') return;
    const loading = section.querySelector('[data-lp-reviews-loading]');
    const empty = section.querySelector('[data-lp-reviews-empty]');
    if (!loading || !empty) return;
    let settled = false;
    const settle = () => {
      if (settled) return;
      const hasReview = Boolean(section.querySelector(JUNIP_CONTENT_SELECTOR));
      const hasRenderedWidget = section.querySelector('.junip-review-section-wrapper')?.childElementCount > 0;
      if (hasReview || hasRenderedWidget) {
        settled = true;
        section.classList.toggle('lp-reviews--empty-state', !hasReview);
        loading.hidden = true;
        empty.hidden = hasReview;
      }
    };
    const observer = new MutationObserver(settle);
    observer.observe(section, { childList: true, subtree: true });
    window.setTimeout(() => {
      settle();
      if (!settled) {
        section.classList.add('lp-reviews--empty-state');
        loading.hidden = true;
        empty.hidden = false;
      }
      observer.disconnect();
    }, 8000);
  }

  function setVisible(element, visible) {
    if (element) element.hidden = !visible;
  }

  function messageForReason(reason) {
    const messages = {
      ALREADY_REVIEWED: ['Reseña recibida.', 'Ya registramos una reseña para este pedido. Gracias por compartir tu experiencia.'],
      AWAITING_DELIVERY: ['Todavía hay un envío en camino.', 'Podrás dejar tu reseña cuando todos los paquetes de este pedido aparezcan como entregados.'],
      ORDER_CANCELLED: ['Pedido cancelado.', 'Este pedido no está disponible para reseña.'],
      ORDER_NOT_PAID: ['Pedido pendiente.', 'Este pedido todavía no aparece como pagado.'],
      ORDER_NOT_FOUND: ['No encontramos el pedido.', 'Entra con el mismo correo que usaste al comprar e inténtalo nuevamente.'],
    };
    return messages[reason] || ['Aún no hay una compra disponible.', 'Cuando tengas un pedido elegible podrás compartir aquí tu experiencia.'];
  }

  function formatOrderDate(value) {
    return new Intl.DateTimeFormat('es-MX', { day: '2-digit', month: 'short', year: 'numeric' }).format(new Date(value));
  }

  async function normalizeImage(file) {
    if (!ALLOWED_TYPES.has(file.type) || file.size > MAX_BYTES) {
      throw new Error('Cada foto debe ser JPG, PNG o WebP y pesar máximo 10 MB.');
    }
    if (!('createImageBitmap' in window)) return file;
    try {
      const bitmap = await createImageBitmap(file);
      const scale = Math.min(1, 2400 / Math.max(bitmap.width, bitmap.height));
      const canvas = document.createElement('canvas');
      canvas.width = Math.max(1, Math.round(bitmap.width * scale));
      canvas.height = Math.max(1, Math.round(bitmap.height * scale));
      const context = canvas.getContext('2d', { alpha: true });
      context.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
      bitmap.close();
      const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/webp', 0.86));
      if (!blob) return file;
      const baseName = file.name.replace(/\.[^.]+$/u, '').slice(0, 80) || 'foto';
      return new File([blob], `${baseName}.webp`, { type: 'image/webp', lastModified: Date.now() });
    } catch {
      return file;
    }
  }

  function initReviewForm(shell) {
    if (shell.dataset.initialized === 'true') return;
    shell.dataset.initialized = 'true';
    const proxyBase = shell.dataset.proxyBase;
    const loading = shell.querySelector('[data-review-loading]');
    const authState = shell.querySelector('[data-review-auth]');
    const loginLink = shell.querySelector('[data-review-login]');
    const messageState = shell.querySelector('[data-review-message]');
    const messageCode = shell.querySelector('[data-review-message-code]');
    const messageTitle = shell.querySelector('[data-review-message-title]');
    const messageBody = shell.querySelector('[data-review-message-body]');
    const form = shell.querySelector('[data-review-fields]');
    const orderWrap = shell.querySelector('[data-review-order-wrap]');
    const orderSelect = shell.querySelector('[data-review-order]');
    const bodyField = shell.querySelector('[data-review-body]');
    const bodyCount = shell.querySelector('[data-review-count]');
    const ratingCaption = shell.querySelector('[data-review-rating-caption]');
    const fileInput = shell.querySelector('[data-review-files]');
    const previews = shell.querySelector('[data-review-previews]');
    const error = shell.querySelector('[data-review-error]');
    const submit = shell.querySelector('[data-review-submit]');
    let selectedFiles = [];
    let eligibleOrders = [];

    const orderFromUrl = new URL(window.location.href).searchParams.get('order');
    const eligibilityUrl = new URL(`${proxyBase}/eligibility`, window.location.origin);
    if (orderFromUrl) eligibilityUrl.searchParams.set('order', orderFromUrl);
    const returnUrl = `${window.location.pathname}${window.location.search}`;
    const loginUrl = new URL(shell.dataset.loginUrl, window.location.origin);
    loginUrl.searchParams.set('return_url', returnUrl);
    loginLink.href = loginUrl.toString();

    function showMessage(reason, override) {
      const [title, description] = override || messageForReason(reason);
      messageCode.textContent = reason === 'SERVER_ERROR' ? 'ESTADO / CONEXIÓN' : 'ESTADO / PEDIDO';
      messageTitle.textContent = title;
      messageBody.textContent = description;
      setVisible(loading, false);
      setVisible(authState, false);
      setVisible(form, false);
      setVisible(messageState, true);
    }

    function renderPreviews() {
      previews.replaceChildren();
      selectedFiles.forEach((file, index) => {
        const figure = document.createElement('figure');
        figure.className = 'lp-review-photo-preview';
        const image = document.createElement('img');
        image.src = URL.createObjectURL(file);
        image.alt = `Vista previa de ${file.name}`;
        image.onload = () => URL.revokeObjectURL(image.src);
        const button = document.createElement('button');
        button.type = 'button';
        button.setAttribute('aria-label', `Quitar ${file.name}`);
        button.textContent = '×';
        button.addEventListener('click', () => {
          selectedFiles.splice(index, 1);
          renderPreviews();
        });
        figure.append(image, button);
        previews.append(figure);
      });
    }

    function showForm(orders) {
      eligibleOrders = orders.filter((order) => order.eligible);
      if (!eligibleOrders.length) {
        showMessage(orders[0]?.reason || 'NO_ELIGIBLE_ORDER');
        return;
      }
      orderSelect.replaceChildren();
      eligibleOrders.forEach((order) => {
        const option = document.createElement('option');
        option.value = order.id;
        option.textContent = `${order.name} · ${formatOrderDate(order.purchasedAt)}`;
        orderSelect.append(option);
      });
      setVisible(orderWrap, eligibleOrders.length > 1);
      setVisible(loading, false);
      setVisible(authState, false);
      setVisible(messageState, false);
      setVisible(form, true);
    }

    async function loadEligibility() {
      try {
        const response = await fetch(eligibilityUrl, { headers: { Accept: 'application/json' } });
        const payload = await response.json();
        if (response.status === 401 && payload.code === 'AUTH_REQUIRED') {
          setVisible(loading, false);
          setVisible(authState, true);
          return;
        }
        if (!response.ok) {
          showMessage(payload.code, [payload.message || 'No pudimos verificar tu compra.', 'Actualiza la página e inténtalo nuevamente.']);
          return;
        }
        showForm(payload.orders || []);
      } catch {
        showMessage('SERVER_ERROR', ['No pudimos conectarnos.', 'Revisa tu conexión y actualiza la página para intentarlo nuevamente.']);
      }
    }

    bodyField.addEventListener('input', () => {
      bodyCount.textContent = String(bodyField.value.length);
    });
    form.addEventListener('change', (event) => {
      if (event.target.name !== 'rating' || !ratingCaption) return;
      const captions = ['Muy mala', 'Mala', 'Regular', 'Muy buena', 'Excelente'];
      const value = Number(event.target.value);
      ratingCaption.textContent = `${captions[value - 1]} · ${value} de 5`;
    });
    fileInput.addEventListener('change', async () => {
      error.hidden = true;
      const incoming = Array.from(fileInput.files || []);
      if (selectedFiles.length + incoming.length > MAX_FILES) {
        error.textContent = 'Puedes agregar hasta 5 fotografías.';
        error.hidden = false;
        fileInput.value = '';
        return;
      }
      try {
        fileInput.disabled = true;
        for (const file of incoming) selectedFiles.push(await normalizeImage(file));
        renderPreviews();
      } catch (fileError) {
        error.textContent = fileError.message;
        error.hidden = false;
      } finally {
        fileInput.disabled = false;
        fileInput.value = '';
      }
    });

    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      error.hidden = true;
      if (!form.reportValidity()) return;
      const rating = Number(new FormData(form).get('rating'));
      const orderId = orderSelect.value || eligibleOrders[0]?.id;
      submit.disabled = true;
      submit.textContent = selectedFiles.length ? 'SUBIENDO FOTOS…' : 'ENVIANDO…';
      try {
        const uploadReceipts = [];
        if (selectedFiles.length) {
          const manifestResponse = await fetch(`${proxyBase}/uploads`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ orderId, files: selectedFiles.map((file) => ({ name: file.name, type: file.type, size: file.size })) }),
          });
          const manifest = await manifestResponse.json();
          if (!manifestResponse.ok) throw new Error(manifest.message || 'No pudimos preparar las fotografías.');
          for (let index = 0; index < manifest.uploads.length; index += 1) {
            submit.textContent = `SUBIENDO FOTO ${index + 1} DE ${manifest.uploads.length}…`;
            const upload = manifest.uploads[index];
            const uploadBody = new FormData();
            upload.target.parameters.forEach((parameter) => uploadBody.append(parameter.name, parameter.value));
            uploadBody.append('file', selectedFiles[index]);
            const uploadResponse = await fetch(upload.target.url, { method: 'POST', body: uploadBody });
            if (!uploadResponse.ok) throw new Error('Una fotografía no pudo subirse. Intenta nuevamente.');
            uploadReceipts.push(upload.receipt);
          }
        }
        submit.textContent = 'ENVIANDO RESEÑA…';
        const response = await fetch(`${proxyBase}/submissions`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            orderId,
            rating,
            body: bodyField.value,
            consent: shell.querySelector('[data-review-consent]').checked,
            uploadReceipts,
          }),
        });
        const payload = await response.json();
        if (!response.ok) throw new Error(payload.message || 'No pudimos enviar la reseña.');
        showMessage('REVIEW_PENDING', ['Gracias por compartir tu experiencia.', 'Recibimos tu reseña y la revisaremos antes de publicarla.']);
        messageCode.textContent = 'RESEÑA / RECIBIDA';
      } catch (submitError) {
        error.textContent = submitError.message;
        error.hidden = false;
        submit.disabled = false;
        submit.textContent = 'ENVIAR RESEÑA';
      }
    });
    loadEligibility();
  }

  function initReviewLightbox(section) {
    const dialog = section.querySelector('[data-review-lightbox]');
    if (!dialog || dialog.dataset.initialized === 'true') return;
    dialog.dataset.initialized = 'true';
    const image = dialog.querySelector('[data-review-lightbox-image]');
    const counter = dialog.querySelector('[data-review-lightbox-counter]');
    const previous = dialog.querySelector('[data-review-lightbox-previous]');
    const next = dialog.querySelector('[data-review-lightbox-next]');
    const close = dialog.querySelector('[data-review-lightbox-close]');
    let gallery = [];
    let currentIndex = 0;
    let opener;

    function render(index) {
      if (!gallery.length) return;
      currentIndex = (index + gallery.length) % gallery.length;
      const item = gallery[currentIndex];
      const thumbnail = item.querySelector('img');
      image.src = item.dataset.reviewPhotoSrc;
      image.alt = thumbnail?.alt || 'Fotografía ampliada de una reseña';
      counter.textContent = `${currentIndex + 1} / ${gallery.length}`;
      previous.hidden = gallery.length < 2;
      next.hidden = gallery.length < 2;
    }

    function openLightbox(button) {
      opener = button;
      gallery = Array.from(button.closest('.lp-review-card').querySelectorAll('[data-review-photo]'));
      render(gallery.indexOf(button));
      if (typeof dialog.showModal === 'function') dialog.showModal();
      else dialog.setAttribute('open', '');
    }

    section.addEventListener('click', (event) => {
      const button = event.target.closest('[data-review-photo]');
      if (button && section.contains(button)) openLightbox(button);
    });
    previous.addEventListener('click', () => render(currentIndex - 1));
    next.addEventListener('click', () => render(currentIndex + 1));
    close.addEventListener('click', () => dialog.close());
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) dialog.close();
    });
    dialog.addEventListener('close', () => {
      image.removeAttribute('src');
      opener?.focus();
    });
  }

  function initialize(root = document) {
    root.querySelectorAll('[data-lp-reviews]').forEach(syncJunip);
    root.querySelectorAll('[data-lp-reviews]').forEach(initReviewLightbox);
    root.querySelectorAll('[data-lp-review-form]').forEach(initReviewForm);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialize());
  else initialize();
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
})();
